'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Download, ChevronRight } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
      return twMerge(clsx(inputs));
}

const Navbar = () => {
      const [isScrolled, setIsScrolled] = useState(false);
      const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

      useEffect(() => {
            const handleScroll = () => {
                  setIsScrolled(window.scrollY > 20);
            };
            window.addEventListener('scroll', handleScroll);
            return () => window.removeEventListener('scroll', handleScroll);
      }, []);

      const navLinks = [
            { name: 'Ana Sayfa', href: '#' },
            { name: 'Özellikler', href: '#features' },
            { name: 'Markalar', href: '#brands' },
            { name: 'Nasıl Çalışır?', href: '#how-it-works' },
            { name: 'İyi Yaşam Okulu', href: 'iyiYasamOkulu' },
      ];

      const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, href: string) => {
            setIsMobileMenuOpen(false);
            if (href.startsWith('#')) {
                  e.preventDefault();
                  const element = document.querySelector(href);
                  if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                  }
            }
      };

      return (
            <>
                  <motion.nav
                        initial={{ y: -100 }}
                        animate={{ y: 0 }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                        className={cn(
                              "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
                              isScrolled
                                    ? "bg-white backdrop-blur-md border-b border-gray-100/50 py-3 shadow-[0_4px_20px_-5px_rgba(99,32,238,0.05)]"
                                    : "bg-transparent border-transparent py-6"
                        )}
                  >
                        <div className="container mx-auto px-6 flex items-center justify-between">
                              <Link href="/" className="relative w-32 h-10 md:w-40 md:h-12 transition-transform hover:scale-105 duration-300">
                                    <Image
                                          src="/logo.svg"
                                          alt="Gyno Club Logo"
                                          fill
                                          className="object-contain object-left"
                                          priority
                                    />
                              </Link>

                              <div className="hidden md:flex items-center gap-10">
                                    {navLinks.map((link) => (
                                          <a
                                                key={link.name}
                                                href={link.href}
                                                onClick={(e) => handleLinkClick(e, link.href)}
                                                className="group relative py-2 cursor-pointer"
                                          >
                                                <span className={cn(
                                                      "text-sm font-semibold transition-colors duration-300",
                                                      isScrolled
                                                            ? "text-gray-600 group-hover:text-[#6320EE]"
                                                            : "text-gray-800 group-hover:text-[#6320EE]"
                                                )}>
                                                      {link.name}
                                                </span>
                                                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-linear-to-r from-[#6320EE] to-[#DA38E2] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left" />
                                          </a>
                                    ))}
                              </div>

                              <div className="hidden md:block">
                                    <Link
                                          href="https://gynoclub.com.tr/app"
                                          target="_blank"
                                          className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full bg-linear-to-r from-[#6320EE] to-[#DA38E2] text-white font-bold text-sm shadow-lg shadow-purple-500/25 hover:shadow-purple-500/50 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                                    >
                                          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                                          <Download className="w-4 h-4 relative z-10" />
                                          <span className="relative z-10">Uygulamayı İndir</span>
                                    </Link>
                              </div>

                              <button
                                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                    className="md:hidden p-2 text-gray-700 hover:text-[#6320EE] transition-colors"
                              >
                                    {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
                              </button>
                        </div>
                  </motion.nav>

                  <AnimatePresence>
                        {isMobileMenuOpen && (
                              <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="fixed inset-0 z-40 bg-white/95 backdrop-blur-xl md:hidden"
                              >
                                    <div className="absolute top-[-20%] right-[-20%] w-96 h-96 bg-purple-200/30 rounded-full blur-3xl pointer-events-none animate-spin-slow" />
                                    <div className="absolute bottom-[-20%] left-[-20%] w-96 h-96 bg-pink-200/30 rounded-full blur-3xl pointer-events-none animate-spin-slow" style={{ animationDirection: 'reverse' }} />

                                    <div className="flex flex-col h-full justify-center px-8 relative z-10">
                                          <div className="flex flex-col gap-4">
                                                {navLinks.map((link, i) => (
                                                      <motion.div
                                                            key={link.name}
                                                            initial={{ opacity: 0, x: -30 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.1 + i * 0.1, ease: "easeOut" }}
                                                      >
                                                            <a
                                                                  href={link.href}
                                                                  onClick={(e) => handleLinkClick(e, link.href)}
                                                                  className="group flex items-center justify-between text-3xl font-extrabold text-gray-900 hover:text-transparent hover:bg-clip-text hover:bg-linear-to-r hover:from-[#6320EE] hover:to-[#DA38E2] transition-all py-4 border-b border-gray-100"
                                                            >
                                                                  {link.name}
                                                                  <ChevronRight className="w-8 h-8 text-gray-300 group-hover:text-[#DA38E2] transition-colors" />
                                                            </a>
                                                      </motion.div>
                                                ))}

                                                <motion.div
                                                      initial={{ opacity: 0, y: 30 }}
                                                      animate={{ opacity: 1, y: 0 }}
                                                      transition={{ delay: 0.5, ease: "easeOut" }}
                                                      className="mt-8"
                                                >
                                                      <Link
                                                            href="https://gynoclub.com.tr/app"
                                                            target="_blank"
                                                            className="flex items-center justify-center gap-3 w-full py-5 rounded-2xl bg-linear-to-r from-[#6320EE] to-[#DA38E2] text-white font-bold text-xl shadow-xl shadow-purple-500/30 active:scale-95 transition-transform"
                                                      >
                                                            <Download className="w-6 h-6" />
                                                            Hemen İndir
                                                      </Link>
                                                </motion.div>
                                          </div>
                                    </div>
                              </motion.div>
                        )}
                  </AnimatePresence>
            </>
      );
};

export default Navbar;